import { useState, useEffect } from "react"
import { getSessions, updateSession, deleteSession } from "../api/session"
import { motion } from "framer-motion"

interface Props {
  language: "tr" | "en"
  onClose: () => void
}

export const HistoryView = ({ language, onClose }: Props) => {
  const [sessions, setSessions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    getSessions(90).then(data => { setSessions(data); setLoading(false) })
  }, [])

  const handleOutcome = async (sessionId: string, outcome: string) => {
    setSavingId(sessionId)
    await updateSession(sessionId, { outcome } as any)
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, outcome } : s))
    setSavingId(null)
  }

  const handleDelete = async (sessionId: string) => {
    const msg = language === "tr" ? "Bu kaydı silmek istiyor musun?" : "Delete this session?"
    if (!window.confirm(msg)) return
    setDeletingId(sessionId)
    const ok = await deleteSession(sessionId)
    if (ok) setSessions(prev => prev.filter(s => s.id !== sessionId))
    setDeletingId(null)
  }

  const outcomeConfig: Record<string, { labelEN: string; labelTR: string; color: string; activeClass: string }> = {
    done:      { labelEN: "Done",    labelTR: "Yaptım",    color: "#6db89a", activeClass: "active-done" },
    gave_up:   { labelEN: "Gave up", labelTR: "Vazgeçtim", color: "#e87a6a", activeClass: "active-gave-up" },
    postponed: { labelEN: "Later",   labelTR: "Erteledim", color: "#a78bfa", activeClass: "active-postponed" },
  }

  const t = language === "tr" ? {
    title: "Son 90 Gün", empty: "Henüz kayıt yok.", close: "← Geri",
    outcomeLabel: "Ne oldu?", also: "Ayrıca: ", deleteBtn: "Sil",
  } : {
    title: "Last 90 Days", empty: "No sessions yet.", close: "← Back",
    outcomeLabel: "How did it go?", also: "Also: ", deleteBtn: "Delete",
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString(language === "tr" ? "tr-TR" : "en-US", { day: "numeric", month: "short", weekday: "short" })
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7280" }}>
          {t.title}
        </div>
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#6b7280", fontSize: "14px", cursor: "pointer", fontFamily: "inherit" }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#9ca3af"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
        >
          {t.close}
        </button>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "40px 0", color: "#6b7280", fontSize: "14px" }}>...</div>}

      {!loading && sessions.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#6b7280", fontSize: "15px" }}>{t.empty}</div>
      )}

      {!loading && sessions.map((s, i) => {
        const outcome = s.outcome ? outcomeConfig[s.outcome] : null
        const isSaving = savingId === s.id
        const isDeleting = deletingId === s.id

        return (
          <motion.div
            key={s.id}
            className="history-card"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: isDeleting ? 0 : 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.3 }}
          >
            <div className="outcome-row">
              <div className="history-date">{formatDate(s.session_date)}</div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {outcome && (
                  <span className="outcome-badge" style={{ color: outcome.color }}>
                    {language === "tr" ? outcome.labelTR : outcome.labelEN}
                  </span>
                )}
                <button
                  onClick={() => handleDelete(s.id)}
                  disabled={isDeleting}
                  style={{ background: "transparent", border: "none", color: "#4b5563", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#e87a6a"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "#4b5563"}
                >
                  {t.deleteBtn}
                </button>
              </div>
            </div>

            {s.focus_decision && <div className="history-decision">{s.focus_decision}</div>}
            {s.action_plan && <div className="history-action">{s.action_plan}</div>}

            {s.important_items && s.important_items.filter((d: string) => d !== s.focus_decision).length > 0 && (
              <div className="history-also">
                {t.also}{s.important_items.filter((d: string) => d !== s.focus_decision).join(", ")}
              </div>
            )}

            <div className="outcome-buttons">
              {Object.entries(outcomeConfig).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => !isSaving && handleOutcome(s.id, key)}
                  disabled={isSaving}
                  className={`outcome-btn ${s.outcome === key ? cfg.activeClass : ""}`}
                >
                  {language === "tr" ? cfg.labelTR : cfg.labelEN}
                </button>
              ))}
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}